import { h } from "vue";
import { POSITION, TYPE } from "vue-toastification";
import Minerve from "./Minerve";
import type { Expression, Mouvement } from "./minerve-dessin";

/**
 * Minerve dans les toasts (charte, section 08).
 *
 * Les textes restent ceux du produit, émis un peu partout par `useToast()`. On n'y touche pas :
 * la configuration globale de `vue-toastification` pose l'icône au moment de la création, d'après
 * le texte (les 17 messages de la charte) ou, à défaut, d'après le type.
 */
export type ExpressionToast = {
  expression: Expression;
  mouvements: readonly Mouvement[];
};

type Entree = ExpressionToast & { motif: string | RegExp };

/** Les messages cités par la charte. Les motifs en expression régulière couvrent les textes à trou. */
export const TOASTS_CHARTE: readonly Entree[] = [
  {
    motif: "Veille ajoutée avec succès.",
    expression: "clin",
    mouvements: ["nod"],
  },
  {
    motif: "Veille modifiée avec succès.",
    expression: "neutre",
    mouvements: ["nod"],
  },
  {
    motif: "Veille supprimée avec succès.",
    expression: "clin",
    mouvements: ["flyaway"],
  },
  {
    motif: "Récapitulatif créé avec succès.",
    expression: "lecture",
    mouvements: ["read"],
  },
  {
    motif: "Récapitulatif modifié avec succès.",
    expression: "neutre",
    mouvements: ["nod"],
  },
  {
    motif: "Récapitulatif supprimé avec succès.",
    expression: "clin",
    mouvements: ["flyaway"],
  },
  {
    motif: "Votre abonnement a été mis à jour avec succès !",
    expression: "surpris",
    mouvements: ["startle"],
  },
  {
    motif: /^P[ée]tition (créée|ajoutée) avec succès/,
    expression: "trouve",
    mouvements: ["hop"],
  },
  {
    motif: "Aucun changement à enregistrer",
    expression: "neutre",
    mouvements: ["blink"],
  },
  {
    motif: "Votre abonnement n'a pas été mis à jour.",
    expression: "triste",
    mouvements: ["breath"],
  },
  {
    motif: /^Votre session a expir/,
    expression: "dort",
    mouvements: ["breath"],
  },
  {
    motif: /^Une erreur serveur est survenue/,
    expression: "etourdi",
    mouvements: ["flicker"],
  },
  {
    motif: /^Limite de mots-clés pour une requête atteinte/,
    expression: "surpris",
    mouvements: ["shake"],
  },
  {
    motif: /^L'alerte n'a pas été sauvegardée car elle est trop large/,
    expression: "perplexe",
    mouvements: ["tilt"],
  },
  {
    motif: "Votre abonnement ne vous donne pas accès à cette section.",
    expression: "severe",
    mouvements: ["shake"],
  },
  {
    motif: /^Cette adresse email est déjà utilisée/,
    expression: "perplexe",
    mouvements: ["blink"],
  },
  {
    motif: "Pas de compte associé à cet email.",
    expression: "perplexe",
    mouvements: ["blink"],
  },
];

/** Un défaut par type pour tous les autres messages (succès, info, avertissement, erreur). */
export const TOASTS_PAR_TYPE: Record<string, ExpressionToast> = {
  [TYPE.SUCCESS]: { expression: "clin", mouvements: ["nod"] },
  [TYPE.INFO]: { expression: "neutre", mouvements: ["blink"] },
  [TYPE.WARNING]: { expression: "triste", mouvements: ["breath"] },
  [TYPE.ERROR]: { expression: "perplexe", mouvements: ["blink"] },
  [TYPE.DEFAULT]: { expression: "neutre", mouvements: ["blink"] },
};

export function minerveDuToast(
  type: string,
  contenu: unknown
): ExpressionToast {
  if (typeof contenu === "string") {
    const trouve = TOASTS_CHARTE.find(({ motif }) =>
      typeof motif === "string" ? motif === contenu : motif.test(contenu)
    );
    if (trouve) return trouve;
  }
  return TOASTS_PAR_TYPE[type] ?? TOASTS_PAR_TYPE[TYPE.DEFAULT];
}

type ToastEnCreation = { type: string; content: unknown; icon?: unknown };

/** À brancher sur l'option `filterBeforeCreate` de `vue-toastification`. */
export function poserMinerveSurLeToast<T extends ToastEnCreation>(toast: T): T {
  const { expression, mouvements } = minerveDuToast(toast.type, toast.content);
  toast.icon = {
    render: () =>
      h(Minerve, { taille: 44, detail: "mid", expression, mouvements }),
  };
  return toast;
}

/**
 * Options globales : en bas au centre comme avant, succès et info 5 s, avertissement 5 s,
 * erreurs jusqu'à fermeture (charte, section 08).
 */
export const optionsToasts = {
  position: POSITION.BOTTOM_CENTER,
  filterBeforeCreate: poserMinerveSurLeToast,
  toastDefaults: {
    [TYPE.SUCCESS]: { timeout: 5000 },
    [TYPE.INFO]: { timeout: 5000 },
    [TYPE.ERROR]: { timeout: false as const },
  },
};
